import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LiveQA from '../components/LiveQA';
import { Radio, Share2, Lock, ArrowLeft, ArrowRight, Loader2, Mic2 } from 'lucide-react';
import { cn, formatTime, getDurationInSeconds, normalizeSpeakers } from '../utils';
import { getTheme } from '../utils/themes';
import { useRoom } from '../hooks/useRoom';

const LiveRoom = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    roomData,
    sessionState,
    timeLeft,
    reactions,
    isUnlocked,
    showPasswordPrompt,
    accessError,
    isInitialLoading,
    isModerator,
    handlePasswordSubmit,
    handleReaction,
    handleInvite
  } = useRoom(id);

  const [passwordInput, setPasswordInput] = useState('');
  const theme = getTheme(roomData?.type || 'qa');

  const onPasswordSubmit = (e) => {
    e.preventDefault();
    handlePasswordSubmit(passwordInput);
  };

  if (isInitialLoading || !roomData) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className={cn("animate-spin mx-auto mb-4", theme.text)} />
          <p className="text-gray-500 font-medium mb-4">Preparing Session...</p>
          <button onClick={() => navigate(-1)} className={cn("font-bold text-sm", theme.text)}>Go Back</button>
        </div>
      </div>
    );
  }

  const expectedDurationSeconds = getDurationInSeconds(roomData?.settings?.duration);

  return (
    <div className="min-h-screen bg-brand-light">
      <AnimatePresence>
        {showPasswordPrompt && !isUnlocked && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-brand-light flex items-center justify-center px-4"
          >
            <div className="max-w-md w-full bg-white rounded-3xl border border-gray-100 p-8 shadow-2xl shadow-brand-indigo/5 text-center">
              <div className={cn("w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-6", theme.lightBg, theme.text)}>
                <Lock size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{roomData.type} Session</h2>
              <p className="text-gray-500 font-medium mb-8">This session is password protected. Please enter the access code to join.</p>
              
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <input 
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter password"
                    className={cn(
                      "w-full px-6 py-4 bg-gray-50 border-2 rounded-xl font-bold transition-all text-center text-xl tracking-widest outline-none",
                      theme.ring,
                      accessError ? "border-red-100 bg-red-50 text-red-600 focus:ring-red-100" : cn("border-gray-50", `focus:${theme.border}`)
                    )}
                    autoFocus
                  />
                  {accessError && (
                    <p className="text-red-500 text-xs font-bold mt-3 animate-in fade-in slide-in-from-top-1">{accessError}</p>
                  )}
                </div>
                <button 
                  type="submit"
                  className={cn("w-full text-white font-bold py-4 rounded-2xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2", theme.bg, theme.shadow, theme.hover)}
                >
                  Access Session
                  <ArrowRight size={20} />
                </button>
                <button 
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full text-gray-400 font-bold py-2 text-sm hover:text-gray-600 transition-colors"
                >
                  Go Back
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed inset-0 pointer-events-none z-50">
        <AnimatePresence>
          {reactions.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: '80vh', x: `${r.x}vw` }}
              animate={{ opacity: 1, y: '20vh' }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="absolute text-4xl"
            >
              {r.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 md:py-3 flex items-center justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className={cn("p-1.5 md:p-2 hover:bg-gray-100 rounded-xl transition-colors active:scale-95 shrink-0", theme.text)}>
              <ArrowLeft size={18} className="md:w-5 md:h-5" />
            </button>
            <div className={cn("w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center text-white shadow-lg shrink-0", theme.bg, theme.shadow)}>
               <Radio size={18} className="md:w-5 md:h-5 animate-pulse" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 md:gap-2 mb-0.5">
                <span className={cn("hidden xs:inline-block text-[8px] md:text-[9px] font-bold px-1 md:px-1.5 py-0.5 rounded uppercase tracking-[0.15em] border shrink-0", theme.lightBg, theme.text, theme.lightBorder)}>Live</span>
                <h1 className="text-sm md:text-lg font-bold text-gray-900 truncate max-w-[120px] xs:max-w-[200px] sm:max-w-none">{roomData.title}</h1>
              </div>
              <p className="text-[9px] md:text-xs text-gray-500 font-medium truncate opacity-80">
                {roomData.description || `Hosted by ${roomData.host || 'Host'} • ${roomData.participants || 0} participants`}
              </p>
              
              {/* Added Speaker List Inline */}
              {normalizeSpeakers(roomData?.speakers || roomData?.settings?.speakers).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1 md:mt-2">
                   {normalizeSpeakers(roomData?.speakers || roomData?.settings?.speakers).map((speaker, index) => (
                     <div key={index} className={cn("flex items-center gap-1 px-1.5 py-0.5 rounded-xl border shadow-sm transition-transform hover:scale-105", theme.lightBg, theme.text, theme.lightBorder)}>
                        <Mic2 size={8} className="md:w-2.5 md:h-2.5 opacity-70" />
                        <span className="text-[8px] md:text-[9px] font-bold whitespace-nowrap">{speaker}</span>
                     </div>
                   ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 md:gap-4 shrink-0">
            <div className={cn(
              "flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 md:py-2 rounded-xl border font-bold text-[9px] md:text-xs shadow-sm",
              sessionState === 'upcoming' ? "bg-amber-50 text-amber-600 border-amber-100" : 
              (expectedDurationSeconds > 0 && timeLeft > expectedDurationSeconds) ? "bg-orange-50 text-orange-600 border-orange-100" : cn(theme.lightBg, theme.text, theme.lightBorder)
            )}>
              <div className={cn(
                "w-1.5 h-1.5 md:w-2 md:h-2 rounded-full animate-pulse shadow-[0_0_8px_currentColor]",
                sessionState === 'upcoming' ? "bg-amber-400" : (expectedDurationSeconds > 0 && timeLeft > expectedDurationSeconds) ? "bg-orange-500" : theme.bg
              )} />
              <span className="tabular-nums">{formatTime(timeLeft)}</span>
            </div>
            <button 
              onClick={handleInvite}
              className={cn("p-1.5 md:p-2.5 rounded-xl transition-all shadow-sm", theme.lightBg, theme.text, `hover:${theme.bg}`, "hover:text-white", "hover:shadow-md active:scale-95")}
              title="Share Session"
            >
              <Share2 size={16} className="md:w-[18px] md:h-[18px]" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <LiveQA 
          sessionState={sessionState} 
          isModerator={isModerator} 
          sessionId={roomData?.id}
          communityId={roomData?.community_id}
          typingCount={roomData.typing_count || 0}
          roomData={roomData}
        />
      </div>

      <div className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-40 flex flex-row md:flex-col gap-2">
        {['🔥', '❤️', '👏', '😮'].map((emoji) => (
          <button 
            key={emoji}
            onClick={() => handleReaction(emoji)}
            disabled={sessionState !== 'live'}
            className={cn(
              "w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 bg-white border rounded-2xl shadow-xl flex items-center justify-center text-xl md:text-2xl transition-all active:scale-95 disabled:opacity-50",
              theme.lightBorder,
              `hover:${theme.lightBg}`
            )}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LiveRoom;
